import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LoginComponent } from './components/login/login.component';
import { UsersComponent } from './components/users/users.component';
import { DefaultComponent } from './components/default/default.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppMaterialModule } from './modules/app-material/app-material.module';
import { HeaderComponent } from './components/header/header.component';
import { FooterComponent } from './components/footer/footer.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { AngularFireAuthModule } from "@angular/fire/auth";
import { AngularFireModule } from '@angular/fire';
import { AngularFireStorageModule } from '@angular/fire/storage';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FlexLayoutModule } from '@angular/flex-layout';
import { DataService } from './services/data.service';
import { FirebaseStorageService } from './services/firebase-storage.service';
import { NgxQRCodeModule } from '@techiediaries/ngx-qrcode';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { NgxDocViewerModule } from 'ngx-doc-viewer';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { CdkColumnDef } from '@angular/cdk/table';
import { TemplatesComponent } from './components/templates/templates.component';
import { NgxEditorModule } from 'ngx-editor';
import { MatTooltipModule } from '@angular/material/tooltip';
import { YearperiodsComponent } from './components/yearperiods/yearperiods.component';
import { CatalogsComponent } from './components/catalogs/catalogs.component';
import { ProductsComponent } from './components/products/products.component';
import { SchoolsComponent } from './components/schools/schools.component';
import { StagesComponent } from './components/stages/stages.component';
import { GradesComponent } from './components/grades/grades.component';
import { LinesComponent } from './components/lines/lines.component';
import { StudentPhotosComponent } from './components/student-photos/student-photos.component';
import { RelationshipsComponent } from './components/relationships/relationships.component'
import { AngularEditorModule } from '@kolkov/angular-editor';
import { StudentsComponent } from './components/students/students.component';
import { GridModule, EditService, ToolbarService, SortService } from '@syncfusion/ej2-angular-grids';
import { ProductPhotoModalComponent } from './components/dialogs/product-photo-modal/product-photo-modal.component';
import { OrderFamilyComponent } from './components/order-family/order-family.component';
import { OrderAdminComponent } from './components/order-admin/order-admin.component';
import { WelcomeComponent } from './components/welcome/welcome.component';
import { ResetPasswordComponent } from './components/reset-password/reset-password.component';
import { ResetPasswordDialogComponent } from './components/dialogs/reset-password-dialog/reset-password-dialog.component';
import { ResponseOkComponent } from './components/redsys-response/response-ok/response-ok.component';
import { ResponseKoComponent } from './components/redsys-response/response-ko/response-ko.component';
import { ShoppingCartComponent } from './components/shopping-cart/shopping-cart.component';
import { ConfirmReservationDialogComponent } from './components/dialogs/confirm-reservation-dialog/confirm-reservation-dialog.component';
import { SpinnerOverlayComponent } from './components/dialogs/spinner-overlay/spinner-overlay.component';
import { HowItWorksComponent } from './components/how-it-works/how-it-works.component';
import { UploadStudentsComponent } from './components/upload-students/upload-students.component';
import { DndDirective } from './dnd.directive';
import { ProgressComponent } from './components/progress/progress.component';
import { UploadImagesComponent } from './components/upload-images/upload-images.component';
import { SendNotificationsComponent } from './components/send-notifications/send-notifications.component';
import { CustomPopupComponent } from './components/dialogs/custom-popup/custom-popup.component';
import { RelationshipschoolusersComponent } from './components/relationshipschoolusers/relationshipschoolusers.component';
import { RelationshipuserschoolsComponent } from './components/relationshipuserschools/relationshipuserschools.component';
import { RangeDateComponent } from './components/dialogs/range-date/range-date.component';
import {MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS} from '@angular/material-moment-adapter';
import {DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE} from '@angular/material/core';
import {MatDatepickerInputEvent} from '@angular/material/datepicker';
import { ReportagesComponent } from './components/reportages/reportages.component';
import { UploadReportageRelationshipsComponent } from './components/upload-reportage-relationships/upload-reportage-relationships.component';
import { UploadReportageImagesComponent } from './components/upload-reportage-images/upload-reportage-images.component';
import { RelationshipsUserReportagesComponent } from './components/relationships-user-reportages/relationships-user-reportages.component';
import { ReportagesPhotosComponent } from './components/reportages-photos/reportages-photos.component';
import { OrdersReportagesComponent } from './components/orders-reportages/orders-reportages.component';
import { ReportagePhotosDetailsComponent } from './components/reportage-photos-details/reportage-photos-details.component';
import { MonthpickerComponent } from './components/dialogs/monthpicker/monthpicker.component';
import { ConfirmPopupComponent } from './components/dialogs/confirm-popup/confirm-popup.component';
import { AddSonPopupComponent } from './components/dialogs/add-son-popup/add-son-popup.component';
import { NoAccountComponent } from './components/login/no-account/no-account.component';
import { UnsubscribeUserComponent } from './components/unsubscribe-user/unsubscribe-user.component';

export const MY_DATA_FORMATS = {
  parse: { dateInput: 'YYYY-MM-DD', },
  display: {
    dateInput: 'YYYY-MM-DD',
    monthYearLabel: 'YYYY MMM',
    dateA11yLabel: 'YYYY-MM-DD',
    monthYearA11yLabel: 'YYYY MMM',
  },
};

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

const modules: any[] = [
  BrowserModule,
  HttpClientModule,
  AppRoutingModule,
  BrowserAnimationsModule,
  AppMaterialModule,
  AngularFireModule.initializeApp(environment.firebaseConfig),
  AngularFireStorageModule,
  AngularFireAuthModule,
  FormsModule,
  ReactiveFormsModule,
  FlexLayoutModule,
  AngularEditorModule,
  NgxQRCodeModule,
  PdfViewerModule,
  MatTooltipModule,
  GridModule,
  NgxEditorModule.forRoot({
    locals: {
      // menu
      bold: 'Bold',
      italic: 'Italic',
      code: 'Code',
      underline: 'Underline',
      strike: 'Strike',
      blockquote: 'Blockquote',
      bullet_list: 'Bullet List',
      ordered_list: 'Ordered List',
      heading: 'Heading',
      h1: 'Title 1',
      h2: 'Header 2',
      h3: 'Header 3',
      h4: 'Header 4',
      h5: 'Header 5',
      h6: 'Header 6',
      label: 'Parraf',
      align_left: 'Left Align',
      align_center: 'Center Align',
      align_right: 'Right Align',
      align_justify: 'Justify',
      text_color: 'Text Color',
      background_color: 'Background Color',

      // pupups, forms, others...
      url: 'URL',
      text: 'Text',
      openInNewTab: 'Open in new tab',
      insert: 'Insert',
      altText: 'Alt Text',
      title: 'Title',
      remove: 'Remove',
    },
  }),
  NgxDocViewerModule,
  TranslateModule.forRoot({
    loader: {
      provide: TranslateLoader,
      useFactory: (createTranslateLoader),
      deps: [HttpClient]
    }
  })
]

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    UsersComponent,
    HeaderComponent,
    FooterComponent,
    SidebarComponent,
    DefaultComponent,
    TemplatesComponent,
    YearperiodsComponent,
    CatalogsComponent,
    ProductsComponent,
    SchoolsComponent,
    StagesComponent,
    GradesComponent,
    LinesComponent,
    StudentPhotosComponent,
    RelationshipsComponent,
    StudentsComponent,
    ProductPhotoModalComponent,
    OrderFamilyComponent,
    OrderAdminComponent,
    WelcomeComponent,
    ResetPasswordComponent,
    ResetPasswordDialogComponent,
    ResponseOkComponent,
    ResponseKoComponent,
    ShoppingCartComponent,
    ConfirmReservationDialogComponent,
    SpinnerOverlayComponent,
    HowItWorksComponent,
    UploadStudentsComponent,
    DndDirective,
    ProgressComponent,
    UploadImagesComponent,
    SendNotificationsComponent,
    CustomPopupComponent,
    RelationshipschoolusersComponent,
    RelationshipuserschoolsComponent,
    RangeDateComponent,
    ReportagesComponent,
    UploadReportageRelationshipsComponent,
    UploadReportageImagesComponent,
    RelationshipsUserReportagesComponent,
    ReportagesPhotosComponent,
    OrdersReportagesComponent,
    ReportagePhotosDetailsComponent,
    MonthpickerComponent,
    ConfirmPopupComponent,
    AddSonPopupComponent,
    NoAccountComponent,
    UnsubscribeUserComponent
  ],
  imports: [...modules],
  exports: [...modules],
  providers: [DataService, EditService, ToolbarService, SortService, HeaderComponent, FirebaseStorageService, CdkColumnDef,
    {provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]},
    {provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    {provide: MAT_DATE_FORMATS, useValue: MY_DATA_FORMATS}
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
